import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { createLead } from '../../lib/api';
import { toast } from 'sonner';

const BUYER_TYPES = ['Retailer', 'Wholesaler', 'Reseller', 'Corporate', 'Export'];

export const LeadForm = ({
  sourcePageUrl,
  interestCategoryId,
  interestBrandId,
  interestProductId,
  title,
  subtitle,
  showMessage = true,
  showBuyerType = false,
  className = '',
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    buyer_type: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await createLead({
        ...formData,
        buyer_type: formData.buyer_type || null,
        source_page_url: sourcePageUrl || window.location.href,
        interest_category_id: interestCategoryId || null,
        interest_brand_id: interestBrandId || null,
        interest_product_id: interestProductId || null,
      });

      // Track conversion event
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'lead_form_submit', {
          source_page: sourcePageUrl,
        });
      }

      toast.success('Thank you! We will contact you soon.');
      setFormData({ name: '', company: '', email: '', phone: '', buyer_type: '', message: '' });
      navigate('/thank-you');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
      console.error('Lead submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${className}`} data-testid="lead-form-inline">
      {(title || subtitle) && (
        <div className="mb-8">
          {title && <h3 className="heading-md mb-2">{title}</h3>}
          {subtitle && <p className="text-gray-500">{subtitle}</p>}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="inline-name" className="label-text">Name *</Label>
            <Input
              id="inline-name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="input-luxury"
              placeholder="Your name"
              data-testid="inline-lead-name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="inline-company" className="label-text">Company</Label>
            <Input
              id="inline-company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              className="input-luxury"
              placeholder="Company name"
              data-testid="inline-lead-company"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="inline-email" className="label-text">Email *</Label>
            <Input
              id="inline-email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="input-luxury"
              placeholder="your@email.com"
              data-testid="inline-lead-email"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="inline-phone" className="label-text">Phone *</Label>
            <Input
              id="inline-phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              required
              className="input-luxury"
              placeholder="+91 98765 43210"
              data-testid="inline-lead-phone"
            />
          </div>
        </div>

        {showBuyerType && (
          <div className="space-y-2">
            <Label className="label-text">Buyer Type</Label>
            <Select 
              value={formData.buyer_type} 
              onValueChange={(val) => setFormData({ ...formData, buyer_type: val })}
            >
              <SelectTrigger className="input-luxury">
                <SelectValue placeholder="Select your business type" />
              </SelectTrigger>
              <SelectContent>
                {BUYER_TYPES.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {showMessage && (
          <div className="space-y-2">
            <Label htmlFor="inline-message" className="label-text">Message</Label>
            <Textarea
              id="inline-message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              className="input-luxury min-h-[120px] resize-none"
              placeholder="Tell us about your requirements..."
              data-testid="inline-lead-message"
            />
          </div>
        )}

        <Button
          type="submit"
          disabled={loading}
          className="btn-primary w-full sm:w-auto"
          data-testid="inline-lead-submit"
        >
          {loading ? 'Submitting...' : 'Submit Request'}
        </Button>
      </form>
    </div>
  );
};

export default LeadForm;
